import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy, Loader2, FileText, MessageCircle, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import CosmicBackground from "../components/astro/CosmicBackground";
import api, { formatApiErrorDetail } from "../lib/api";

const Channel = ({ icon: Icon, label, state }) => {
  const status = state?.status || "pending";
  const tone = status === "sent" ? "border-green-300 bg-green-50 text-green-800"
    : status === "failed" ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-tara-teal/25 bg-white/70 text-tara-muted";
  const text = status === "sent" ? "Sent" : status === "failed" ? "Could not send" : "Will be sent shortly";
  return (
    <div className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-semibold ${tone}`}
         data-testid={`delivery-${label.toLowerCase()}`}>
      <Icon className="h-3.5 w-3.5" /> {label}: {text}
    </div>
  );
};

export default function UnlockSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order") || "";
  const reportId = params.get("report") || "";
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const cached = sessionStorage.getItem(`tt_pay_${orderId}`);
      if (cached) setResult(JSON.parse(cached));
      const { data } = await api.get("/astro/payment/result", {
        params: { order_id: orderId, report_id: reportId },
      });
      setResult(data);
      if (data.status === "paid") sessionStorage.setItem(`tt_pay_${orderId}`, JSON.stringify(data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId || !reportId) { navigate("/astrology/kundali"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, reportId]);

  const copy = async () => {
    await navigator.clipboard.writeText(result.unlock_code);
    toast.success("Unlock code copied");
  };

  const openReport = () => {
    localStorage.setItem("tt_astro_pending_code", result.unlock_code);
    navigate(`/astrology/kundali?report=${encodeURIComponent(reportId)}`);
  };

  const paid = result?.status === "paid";

  return (
    <div className="relative" data-testid="unlock-success-page">
      <Seo title="Payment Successful — Your Unlock Code" path="/astrology/unlock-success" noindex />
      <CosmicBackground />

      <section className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        {loading && !result && (
          <div className="glass rounded-[1.6rem] p-10 text-center" data-testid="success-loading">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-tara-tealDeep" />
            <p className="mt-3 text-sm text-tara-body">Confirming your payment…</p>
          </div>
        )}

        {!loading && !paid && result && (
          <div className="glass rounded-[1.6rem] p-8 text-center" data-testid="success-pending">
            <p className="font-serif text-xl font-bold text-tara-ink mb-2">Payment is still being confirmed</p>
            <p className="text-sm text-tara-body leading-relaxed mb-6">{result.message}</p>
            <button onClick={load} className="btn-sacred inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold" data-testid="success-refresh-btn">
              <RefreshCw className="h-4 w-4" /> Check again
            </button>
          </div>
        )}

        {paid && (
          <div className="glass-strong relative overflow-hidden rounded-[1.8rem] p-8 sm:p-10" data-testid="success-card">
            <span className="orb orb-gold h-56 w-56 -top-24 right-0" />
            <div className="relative text-center">
              <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-tara-tealDeep to-tara-teal">
                <CheckCircle2 className="h-8 w-8 text-white" />
              </span>
              <h1 className="font-serif text-3xl font-bold text-tara-ink mb-2">Payment Successful</h1>
              <p className="text-sm text-tara-body mb-8" data-testid="success-amount">
                Your payment of {result.amount_display} has been successfully completed.
              </p>

              <p className="mb-2 text-[11px] uppercase tracking-[0.22em] text-tara-muted">Your unique unlock code</p>
              <div className="mx-auto mb-4 max-w-sm rounded-2xl border border-dashed border-tara-vermilion/60 bg-white/80 px-6 py-5">
                <p className="font-mono text-2xl sm:text-3xl font-bold tracking-[0.18em] text-tara-sindoor" data-testid="success-code">
                  {result.unlock_code}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <button onClick={copy} className="btn-ghost-gold inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold" data-testid="copy-code-btn">
                  <Copy className="h-4 w-4" /> Copy Code
                </button>
                <button onClick={openReport} className="btn-sacred inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold" data-testid="view-report-btn">
                  <FileText className="h-4 w-4" /> View My Report
                </button>
              </div>

              <div className="mt-8 grid gap-3 text-left sm:grid-cols-2">
                <div className="rounded-2xl border border-tara-teal/20 bg-white/70 p-4">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-tara-muted">Report reference</p>
                  <p className="mt-1 font-mono text-sm text-tara-ink" data-testid="success-report-ref">{result.report_ref}</p>
                </div>
                <div className="rounded-2xl border border-tara-teal/20 bg-white/70 p-4">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-tara-muted">Payment status</p>
                  <p className="mt-1 text-sm font-semibold text-green-700" data-testid="success-status">Paid & verified</p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Channel icon={MessageCircle} label="WhatsApp" state={result.delivery?.whatsapp} />
                <Channel icon={Mail} label="Email" state={result.delivery?.email} />
              </div>

              <p className="mt-7 text-xs text-tara-body leading-relaxed">
                Your unique unlock code has been generated successfully. This code is linked to your
                specific Astrology Report — please keep it safe. Enter it on your report page to open the
                complete reading.
              </p>
              <p className="mt-3 text-[11px] text-tara-muted">
                Need help? <Link to="/contact" className="text-tara-tealDeep hover:underline">Contact us</Link> with
                your report reference.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
